import { Injectable } from '@angular/core';
import { ProductListApiService } from '@epicuro-next/data/engagement/engagement-api';
import { ACTIVE_LIST_FAILED_MESSAGE } from '@epicuro-next/state/product-list-state/constants';
import { MessageType, showSnackBarMessage } from '@epicuro-next/state/snackbar';
import { isNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  concatMap,
  map,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';

import * as activeListActions from '../../actions';
import { selectActiveList } from '../../selectors/lists/product-list-active.selectors';

@Injectable()
export class ActiveListEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private productListApi: ProductListApiService,
  ) {}

  /**
   * Effect to set ProductList as active
   */
  public setActiveList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(activeListActions.setActiveList),
      switchMap(({ id }) => {
        return this.productListApi.setActive(id).pipe(
          map(() => activeListActions.setActiveListSuccess({ id })),
          catchError(() => of(activeListActions.setActiveListFail())),
        );
      }),
    ),
  );

  public notifyFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(activeListActions.setActiveListFail),
      map(() =>
        showSnackBarMessage({
          message: ACTIVE_LIST_FAILED_MESSAGE,
          messageType: MessageType.DANGER,
        }),
      ),
    ),
  );

  /**
   * Effect to check the active list and dispatch conditional actions.
   */
  public checkActiveList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(activeListActions.checkActiveList),
      withLatestFrom(this.store.pipe(select(selectActiveList))),
      map(([_, activeList]) => {
        if (activeList) {
          return activeListActions.saveAllItemsInList({ list: activeList });
        }

        return activeListActions.generateList({
          products: [],
        });
      }),
    ),
  );

  /**
   * Effect to patch active list
   */
  public patchActiveProductList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(activeListActions.patchActiveProductList),
      concatMap((action) =>
        of(action).pipe(
          withLatestFrom(this.store.pipe(select(selectActiveList))),
          map(([{ product }, activeList]) => {
            const productExist = activeList?.productsReference?.some(
              (reference) =>
                reference?.productId === product.productId &&
                reference?.brandId === product.brandId,
            );
            return { product, activeList, productExist };
          }),
          switchMap(({ product, activeList, productExist }) => {
            if (isNullish(activeList)) {
              return [
                activeListActions.generateList({
                  products: [action.product],
                }),
              ];
            }

            if (productExist) {
              return [
                activeListActions.deleteProductsFromList({
                  list: activeList,
                  products: [product],
                }),
              ];
            }

            return [
              activeListActions.addProductsToList({
                list: activeList,
                products: [product],
              }),
            ];
          }),
        ),
      ),
    ),
  );
}
